import React, { useCallback, useEffect, useState } from 'react';
import { paymentMethodsAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Plus, Trash2, Wallet, Loader2 } from 'lucide-react';

const TIPOS = [
  { value: 'conferencia', label: 'Entra na conferência do caixa' },
  { value: 'a_receber', label: 'A Receber / Fiado (fora da conferência)' },
];

export const PaymentMethodsSettings = () => {
  const [metodos, setMetodos] = useState([]);
  const [tpagOptions, setTpagOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [novo, setNovo] = useState({ label: '', tpag: '99', tipo: 'conferencia' });

  const carregar = useCallback(async () => {
    try {
      const { data } = await paymentMethodsAPI.list();
      setMetodos(data.payment_methods || []);
      setTpagOptions(data.tpag_options || []);
    } catch (e) {
      toast.error('Erro ao carregar formas de pagamento');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  const criar = async () => {
    if (novo.label.trim().length < 2) {
      toast.error('Informe o nome da forma de pagamento');
      return;
    }
    setSaving(true);
    try {
      await paymentMethodsAPI.create(novo);
      setNovo({ label: '', tpag: '99', tipo: 'conferencia' });
      toast.success('Forma de pagamento criada');
      carregar();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao criar forma de pagamento');
    } finally {
      setSaving(false);
    }
  };

  const atualizar = async (metodo, campos) => {
    setMetodos((prev) => prev.map((m) => (m.id === metodo.id ? { ...m, ...campos } : m)));
    try {
      await paymentMethodsAPI.update(metodo.id, campos);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao atualizar');
      carregar();
    }
  };

  const remover = async (metodo) => {
    if (!window.confirm(`Remover a forma de pagamento "${metodo.label}"?`)) return;
    try {
      await paymentMethodsAPI.remove(metodo.id);
      toast.success('Forma de pagamento removida');
      carregar();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao remover');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-slate-500 p-4">
        <Loader2 className="w-4 h-4 animate-spin" /> Carregando formas de pagamento...
      </div>
    );
  }

  return (
    <div className="border rounded-lg p-4 space-y-4" data-testid="payment-methods-settings">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 bg-emerald-600 rounded-lg flex items-center justify-center shrink-0">
          <Wallet className="w-5 h-5 text-white" />
        </div>
        <div>
          <h3 className="font-semibold">Formas de Pagamento do PDV</h3>
          <p className="text-sm text-slate-500">
            Cadastre as formas que sua loja aceita. Elas aparecem no PDV e entram automaticamente na
            apuração do fechamento de caixa. O código fiscal (tPag) é usado na NFC-e/NF-e.
          </p>
        </div>
      </div>

      <div className="space-y-2">
        {metodos.map((m) => (
          <div
            key={m.id}
            className="flex flex-col md:flex-row md:items-center gap-3 p-3 bg-slate-50 rounded-lg"
            data-testid={`payment-method-row-${m.code}`}
          >
            <Input
              className="md:w-52 bg-white"
              value={m.label}
              onChange={(e) => setMetodos((prev) => prev.map((x) => (x.id === m.id ? { ...x, label: e.target.value } : x)))}
              onBlur={(e) => atualizar(m, { label: e.target.value })}
              data-testid={`payment-method-label-${m.code}`}
            />

            <Select value={m.tpag} onValueChange={(v) => atualizar(m, { tpag: v })}>
              <SelectTrigger className="md:w-64 bg-white" data-testid={`payment-method-tpag-${m.code}`}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {tpagOptions.map((o) => (
                  <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={m.tipo} onValueChange={(v) => atualizar(m, { tipo: v })}>
              <SelectTrigger className="md:w-72 bg-white" data-testid={`payment-method-tipo-${m.code}`}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {TIPOS.map((t) => (
                  <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <div className="flex items-center gap-3 md:ml-auto">
              {m.sistema && <Badge variant="secondary">padrão</Badge>}
              <div className="flex items-center gap-2">
                <Switch
                  checked={!!m.ativo}
                  onCheckedChange={(v) => atualizar(m, { ativo: v })}
                  data-testid={`payment-method-ativo-${m.code}`}
                />
                <span className="text-xs text-slate-500">{m.ativo ? 'Ativa' : 'Inativa'}</span>
              </div>
              {!m.sistema && (
                <Button
                  size="sm"
                  variant="ghost"
                  className="text-rose-600"
                  onClick={() => remover(m)}
                  data-testid={`payment-method-remove-${m.code}`}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              )}
            </div>
          </div>
        ))}
      </div>

      <div className="border-t pt-4 space-y-3">
        <p className="text-sm font-medium">Adicionar nova forma de pagamento</p>
        <div className="flex flex-col md:flex-row gap-3">
          <div className="md:w-52">
            <Label className="text-xs">Nome</Label>
            <Input
              placeholder="Ex: Vale Alimentação"
              value={novo.label}
              onChange={(e) => setNovo({ ...novo, label: e.target.value })}
              data-testid="new-payment-method-label"
            />
          </div>
          <div className="md:w-64">
            <Label className="text-xs">Código fiscal (tPag)</Label>
            <Select value={novo.tpag} onValueChange={(v) => setNovo({ ...novo, tpag: v })}>
              <SelectTrigger data-testid="new-payment-method-tpag">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {tpagOptions.map((o) => (
                  <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="md:w-72">
            <Label className="text-xs">Tipo</Label>
            <Select value={novo.tipo} onValueChange={(v) => setNovo({ ...novo, tipo: v })}>
              <SelectTrigger data-testid="new-payment-method-tipo">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {TIPOS.map((t) => (
                  <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-end">
            <Button
              onClick={criar}
              disabled={saving}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="new-payment-method-save"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
              Adicionar
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentMethodsSettings;
